// src/shared/api/httpClient.js
import { env } from "../../app/config/env";

const TOKEN_KEYS = ["sabi_access_token", "access_token"];
const REFRESH_TOKEN_KEY = "sabi_refresh_token";
const EMPRESA_KEY = "sabi_empresa_id";
const SUCURSAL_KEY = "sabi_sucursal_id";

const SESSION_KEYS = [
  "sabi_access_token",
  "access_token",
  "sabi_refresh_token",
  "sabi_user",
  "sabi_accesos",
  "sabi_empresa",
  "sabi_empresa_id",
  "sabi_sucursal_id",
  "sabi_empresa_nombre",
];

let refreshPromise = null;

function joinUrl(base, path) {
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return `${b}/${p}`;
}

function safeGetLocalStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocalStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    //
  }
}

function safeRemoveLocalStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    //
  }
}

function readToken() {
  for (const k of TOKEN_KEYS) {
    const v = safeGetLocalStorage(k);
    if (v) return v;
  }
  return null;
}

function readRefreshToken() {
  return safeGetLocalStorage(REFRESH_TOKEN_KEY);
}

function clearAllTokens() {
  for (const k of TOKEN_KEYS) safeRemoveLocalStorage(k);
  safeRemoveLocalStorage(REFRESH_TOKEN_KEY);
}

function clearSession() {
  for (const k of SESSION_KEYS) safeRemoveLocalStorage(k);
}

async function parseResponse(response, responseType) {
  if (response.status === 204) return null;

  if (responseType === "blob") {
    return response.blob();
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }
  return response.text();
}

function buildError({ status, data }) {
  let message = "Error de servidor";

  if (typeof data === "string") message = data;
  else if (data?.detail) {
    if (typeof data.detail === "string") message = data.detail;
    else if (Array.isArray(data.detail)) {
      message = data.detail.map((e) => e?.msg || JSON.stringify(e)).join(" | ");
    } else {
      message = JSON.stringify(data.detail);
    }
  } else if (data?.message) {
    if (Array.isArray(data.message)) {
      message = data.message.join(" | ");
    } else {
      message = data.message;
    }
  }

  const err = new Error(message);
  err.status = status;
  err.data = data;
  return err;
}

async function performRefresh() {
  const refreshToken = readRefreshToken();
  if (!refreshToken) {
    throw new Error("No hay refresh token");
  }

  const refreshUrl = joinUrl(env.API_BASE_URL, "/auth/refresh");

  const res = await fetch(refreshUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
    credentials: "include",
  });

  const parsed = await parseResponse(res);

  if (!res.ok || !parsed?.access_token) {
    throw buildError({ status: res.status, data: parsed });
  }

  safeSetLocalStorage("sabi_access_token", parsed.access_token);
  safeRemoveLocalStorage("access_token");

  if (parsed?.refresh_token) {
    safeSetLocalStorage(REFRESH_TOKEN_KEY, parsed.refresh_token);
  }

  return parsed.access_token;
}

async function getFreshToken() {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function request(
  method,
  url,
  { data, headers, signal, responseType, retryOn401 = true } = {}
) {
  const token = readToken();
  const empresaId = safeGetLocalStorage(EMPRESA_KEY);
  const sucursalId = safeGetLocalStorage(SUCURSAL_KEY);

  const finalUrl = url.startsWith("http") ? url : joinUrl(env.API_BASE_URL, url);

  const finalHeaders = {
    ...(data instanceof FormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(empresaId ? { "X-Empresa-Id": String(empresaId) } : {}),
    ...(sucursalId ? { "X-Sucursal-Id": String(sucursalId) } : {}),
    ...(headers || {}),
  };

  const res = await fetch(finalUrl, {
    method,
    headers: finalHeaders,
    body:
      data == null
        ? undefined
        : data instanceof FormData
        ? data
        : JSON.stringify(data),
    credentials: "include",
    signal,
  });

  const parsed = await parseResponse(res, responseType);

  const isAuthRoute =
    finalUrl.includes("/auth/login") ||
    finalUrl.includes("/auth/refresh") ||
    finalUrl.includes("/auth/password-reset/request") ||
    finalUrl.includes("/auth/password-reset/confirm");

  if (res.status === 401 && retryOn401 && !isAuthRoute && readRefreshToken()) {
    try {
      await getFreshToken();
      return request(method, url, {
        data,
        headers,
        signal,
        responseType,
        retryOn401: false,
      });
    } catch {
      clearSession();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      throw buildError({ status: 401, data: parsed });
    }
  }

  if (res.status === 401) {
    clearSession();
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
    throw buildError({ status: 401, data: parsed });
  }

  if (!res.ok) {
    throw buildError({ status: res.status, data: parsed });
  }

  return parsed;
}

const httpClient = {
  get: (url, opts) => request("GET", url, opts),
  post: (url, data, opts) => request("POST", url, { ...(opts || {}), data }),
  put: (url, data, opts) => request("PUT", url, { ...(opts || {}), data }),
  patch: (url, data, opts) => request("PATCH", url, { ...(opts || {}), data }),
  delete: (url, opts) => request("DELETE", url, opts),

  setToken(token) {
    try {
      localStorage.setItem("sabi_access_token", token);
      safeRemoveLocalStorage("access_token");
    } catch {
      //
    }
  },

  setRefreshToken(refreshToken) {
    try {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } catch {
      //
    }
  },

  clearToken() {
    clearAllTokens();
  },

  clearSession,
  getBlob: (url, opts) => request("GET", url, { ...(opts || {}), responseType: "blob" }),
};

export default httpClient;